// TODO: Restyle Sidebar for flat, minimal, clean look
// - Remove box shadows
// - Use white or light gray background
// - Add subtle border, soft corners
// - Use simple icons, clear active state
// - Add lots of whitespace

import React, { useState, useRef, useEffect } from 'react';
import {
  Box, Flex, IconButton, VStack, Tooltip, useColorModeValue,
  useBreakpointValue, Drawer, DrawerOverlay, DrawerContent, DrawerBody,
  DrawerCloseButton, DrawerHeader
} from '@chakra-ui/react';
import {
  FiGrid, FiFileText, FiList, FiAlertCircle, FiSettings, FiUser, FiChevronLeft, FiChevronRight,
  FiCpu, FiTrendingUp, FiUsers, FiBarChart2, FiActivity
} from 'react-icons/fi';
import { Link, useLocation } from 'react-router-dom';
import { FiGitMerge } from 'react-icons/fi';

const NAV_GROUPS = [
  {
    heading: 'Overview',
    items: [
      { to: '/', icon: FiGrid, label: 'Dashboard' },
    ],
  },
  {
    heading: 'Quality',
    items: [
      { to: '/test-case-groups', icon: FiFileText, label: 'Test Results' },
      { to: '/test-runs', icon: FiList, label: 'Test Runs' },
      { to: '/compare', icon: FiGitMerge, label: 'Compare' },
    ],
  },
  {
    heading: 'Analysis',
    items: [
      { to: '/analysis/tickets', icon: FiAlertCircle, label: 'Tickets' },
      { to: '/analysis/ai', icon: FiCpu, label: 'AI Analysis' },
      { to: '/analysis/insights', icon: FiActivity, label: 'Insights' },
      { to: '/analysis/trends', icon: FiTrendingUp, label: 'Trends' },
      { to: '/analysis/impact', icon: FiUsers, label: 'Impact' },
      { to: '/analysis/metrics', icon: FiBarChart2, label: 'Metrics' },
    ],
  },
];

const SETTINGS_ITEM = { to: '/settings', icon: FiSettings, label: 'Settings' };

export default function Sidebar({ isMobileMenuOpen, onCloseMobileMenu, collapsed, setCollapsed }) {
  const location = useLocation();
  const [internalCollapsed, setInternalCollapsed] = useState(false);
  const isMobile = useBreakpointValue({ base: true, md: false });
  const btnRef = useRef();
  
  // Use external collapsed state if provided, otherwise use internal state
  const effectiveCollapsedState = collapsed !== undefined ? collapsed : internalCollapsed;
  const effectiveSetCollapsed = setCollapsed || setInternalCollapsed;

  // Always expanded on mobile
  const effectiveCollapsed = isMobile ? false : effectiveCollapsedState;

  // Reset collapsed to false when switching from mobile to desktop (only once)
  useEffect(() => {
    if (!isMobile && typeof isMobile !== 'undefined') {
      // Only reset if we're coming from mobile (when isMobile was true before)
      // This prevents the sidebar from auto-expanding when user manually collapsed it
      const wasMobile = localStorage.getItem('wasMobile') === 'true';
      if (wasMobile) {
        effectiveSetCollapsed(false);
        localStorage.removeItem('wasMobile');
      }
    } else if (isMobile) {
      // Mark that we're on mobile so we can detect the transition
      localStorage.setItem('wasMobile', 'true');
    }
  }, [isMobile, effectiveSetCollapsed]);

  // Colors
  const sidebarBg = useColorModeValue('white', 'gray.900');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const activeBg = useColorModeValue('green.50', 'green.800');
  const activeColor = useColorModeValue('green.600', 'green.200');
  const iconColor = useColorModeValue('gray.600', 'gray.300');
  const hoverBg = useColorModeValue('gray.100', 'gray.700');

  // Sidebar content
  const sidebarContent = (
    <Flex
      as="nav"
      direction="column"
      h="100vh"
      w={effectiveCollapsed ? '72px' : { base: '100%', md: '220px' }}
      bg={sidebarBg}
      borderRight="1px solid"
      borderColor={borderColor}
      borderRadius="xl"
      // py={6}
      pt={4}
      px={effectiveCollapsed ? 2 : 4}
      transition="all 0.2s"
      position="fixed"
      top="64px"
      left="0"
      zIndex={100}
      overflow="visible"
    >  
      {/* Header section - pinned at top */}
      {/* <Box px={2} py={2} borderBottom="1px solid" borderColor={borderColor}>
        {!effectiveCollapsed && (
          <Box fontSize="lg" fontWeight="bold" color={activeColor} mb={2}>
            Dashboard
          </Box>
        )}
      </Box> */}
      
      {/* Floating collapse/expand button */}
      {!isMobile && (
        <Box
          position="absolute"
          top="20px"
          right={effectiveCollapsed ? "-12px" : "-12px"}
          zIndex={10}
        >
          <IconButton
            icon={effectiveCollapsed ? <FiChevronRight /> : <FiChevronLeft   />}
            aria-label={effectiveCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            onClick={() => {
              console.log(effectiveCollapsed ? 'Expanding sidebar' : 'Collapsing sidebar');
              effectiveSetCollapsed(!effectiveCollapsed);
            }}
            size="sm"
            variant="solid"
            borderRadius="full"
            color={iconColor}
            bg={"white"}
            _hover={{ 
              // bg: activeColor, 
              color: activeColor,
              transform: "scale(1.1)",
              boxShadow: "lg"
            }}
            _active={{
              color: activeColor,
            }}
            transition="all 0.2s"
            boxShadow="md"
            border="2px solid"
            borderColor="gray.100"
          />
        </Box>
      )}
      {/* Main navigation, grouped; scrollable if needed */}
      <Flex 
        direction="column" 
        flex={1} 
        minH={0} 
        overflowY="auto"
        px={2}
        py={2}
        css={{
          '&::-webkit-scrollbar': {
            display: 'none',
          },
          '&::-ms-overflow-style': {
            display: 'none',
          },
          '&': {
            scrollbarWidth: 'none',
          },
        }}
      >
        {NAV_GROUPS.map(group => (
          <Box key={group.heading} mb={effectiveCollapsed ? 2 : 4}>
            {!effectiveCollapsed && (
              <Box px={2} py={1} fontSize="xs" textTransform="uppercase" letterSpacing="wider" color={iconColor}>
                {group.heading}
              </Box>
            )}
            {group.items.map(item => {
              // Special handling for Dashboard (/) - only active when exactly on home page
              const isActive = item.to === '/' 
                ? location.pathname === '/' 
                : location.pathname === item.to || location.pathname.startsWith(item.to);
              return (
                <Tooltip key={item.label} label={item.label} placement="right" isDisabled={!effectiveCollapsed} openDelay={300}>
                  <Box
                    as={Link}
                    to={item.to}
                    display="flex"
                    alignItems="center"
                    w="100%"
                    mb={1}
                    bg={isActive ? activeBg : 'transparent'}
                    borderRadius="lg"
                    _hover={{ bg: hoverBg, color: activeColor }}
                    borderWidth={isActive ? '1px' : '0'}
                    borderColor={isActive ? activeColor : 'transparent'}
                    px={2}
                    py={2}
                    style={{ textDecoration: 'none' }}
                    onClick={isMobile ? onCloseMobileMenu : undefined}
                  >
                    <Box as={item.icon} color={isActive ? activeColor : iconColor} fontSize="1.5rem" mr={effectiveCollapsed ? 0 : 3} />
                    {!effectiveCollapsed && (
                      <Box as="span" color={isActive ? activeColor : iconColor} fontWeight={isActive ? 'bold' : 'normal'} fontSize="md">
                        {item.label}
                      </Box>
                    )}
                  </Box>
                </Tooltip>
              );
            })}
          </Box>
        ))}
      </Flex>
      {/* Bottom section always pinned to bottom */}
      <Box mt="auto" pt={4} px={2} pb={4}>
        <VStack align="stretch" spacing={2}>
          <Tooltip label={SETTINGS_ITEM.label} placement="right" isDisabled={!effectiveCollapsed} openDelay={300}>
            <Box
              as={Link}
              to={SETTINGS_ITEM.to}
              display="flex"
              alignItems="center"
              w="100%"
              mb={1}
              bg={location.pathname.startsWith('/settings') ? activeBg : 'transparent'}
              borderRadius="lg"
              _hover={{ bg: hoverBg, color: activeColor }}
              px={2}
              py={2}
              style={{ textDecoration: 'none' }}
              onClick={isMobile ? onCloseMobileMenu : undefined}
            >
              <Box as={SETTINGS_ITEM.icon} color={location.pathname.startsWith('/settings') ? activeColor : iconColor} fontSize="1.5rem" mr={effectiveCollapsed ? 0 : 3} />
              {!effectiveCollapsed && (
                <Box as="span" color={location.pathname.startsWith('/settings') ? activeColor : iconColor} fontWeight={location.pathname.startsWith('/settings') ? 'bold' : 'normal'} fontSize="md">
                  {SETTINGS_ITEM.label}
                </Box>
              )}
            </Box>
          </Tooltip>
          <Tooltip label="Profile" placement="right" isDisabled={!effectiveCollapsed} openDelay={300}>
            <Box
              as={Link}
              to="/settings"
              display="flex"
              alignItems="center"
              w="100%"
              mb={1}
              bg={'transparent'}
              borderRadius="lg"
              _hover={{ bg: hoverBg, color: activeColor }}
              px={2}
              py={2}
              style={{ textDecoration: 'none' }}
              onClick={isMobile ? onCloseMobileMenu : undefined}
            >
              <Box as={FiUser} color={iconColor} fontSize="1.5rem" mr={effectiveCollapsed ? 0 : 3} />
              {!effectiveCollapsed && (
                <Box as="span" color={iconColor} fontWeight="normal" fontSize="md">
                  Profile
                </Box>
              )}
            </Box>
          </Tooltip>
        </VStack>
      </Box>
    </Flex>
  );

  // Mobile: Drawer
  if (isMobile) {
    return (
      <Drawer 
        isOpen={isMobileMenuOpen} 
        placement="left" 
        onClose={onCloseMobileMenu} 
        finalFocusRef={btnRef} 
        size="xs"
      >
        <DrawerOverlay />
        <DrawerContent bg={sidebarBg}>
          <DrawerCloseButton mt={2} borderRadius="xl" />
          <DrawerHeader borderBottomWidth={0} fontWeight="bold" fontSize="2xl">Menu</DrawerHeader>
          <DrawerBody p={0}>{sidebarContent}</DrawerBody>
        </DrawerContent>
      </Drawer>
    );
  }

  // Desktop sidebar
  return (
    <Box as="aside">
      {sidebarContent}
    </Box>
  );
} 